import React, {
  useMemo,
  useRef,
  useState,
  useLayoutEffect,
  useEffect,
} from "react";
import html2canvas from "html2canvas";

import Modal from "../common/Modal";
import Button from "../common/Button";
import styles from "./InfluenceMapModal.module.css";

const PROCESS_COLORS = {
  emotion: "#4caf50",
  cognition: "#2196f3",
  self: "#9c27b0",
  attention: "#f44336",
};

/* =============================
   COLUNAS
   ============================= */

const COLUMNS = [
  { key: "context", title: "História & Contexto" },
  { key: "emotion", title: "Processos – Emoção" },
  { key: "cognition", title: "Processos – Cognição" },
  { key: "self", title: "Processos – Self" },
  { key: "attention", title: "Processos – Atenção" },
  { key: "values", title: "Valores & Anseios" },
];

/* =============================
   LAYOUT DE ROWS (FIXO E EXPLÍCITO)
   ============================= */

const columnFilter = {
  context: (n) => n.domain === "context",
  emotion: (n) => n.domain === "process" && n.processTag === "emotion",
  cognition: (n) => n.domain === "process" && n.processTag === "cognition",
  self: (n) => n.domain === "process" && n.processTag === "self",
  attention: (n) => n.domain === "process" && n.processTag === "attention",
  values: (n) => n.domain === "values",
};

const NODE_HEIGHT = 40;
const COLUMN_GAP = 12;
const ROW_GAP = 48;
const TOP_PADDING = 56;

/* =============================
   COMPONENTE
   ============================= */

export default function InfluenceMapModal({
  nodes,
  connections,
  columnLayout,
  onChangeColumnLayout,
  onChangeConnections,
  onClose,
}) {
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const boardRef = useRef(null);
  const nodeRefs = useRef({});
  const [boardRect, setBoardRect] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);

      requestAnimationFrame(() => {
        if (boardRef.current) {
          setBoardRect(boardRef.current.getBoundingClientRect());
        }
      });
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () =>
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  const toggleFullscreen = async () => {
    if (!boardRef.current) return;

    if (!document.fullscreenElement) {
      await boardRef.current.requestFullscreen();
      setIsFullscreen(true);
    } else {
      await document.exitFullscreen();
      setIsFullscreen(false);
    }

    // garante que as linhas se ajustem
    requestAnimationFrame(() => {
      if (boardRef.current) {
        setBoardRect(boardRef.current.getBoundingClientRect());
      }
    });
  };

  const exportAsImage = async () => {
    if (!boardRef.current) return;

    const canvas = await html2canvas(boardRef.current, {
      backgroundColor: "#ffffff",
      scale: 2, // melhora qualidade
      useCORS: true,
    });

    const link = document.createElement("a");
    link.download = "fieldmap.png";
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  /* =============================
     MEDIR BOARD
     ============================= */

  useLayoutEffect(() => {
    if (!boardRef.current) return;

    const update = () => {
      setBoardRect(boardRef.current.getBoundingClientRect());
    };

    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  /* =============================
     NODES POR COLUNA
     ============================= */

  const nodesByColumn = useMemo(() => {
    return COLUMNS.map((col) => ({
      ...col,
      row: columnLayout[col.key]?.row ?? 0,
      nodes: nodes.filter(columnFilter[col.key]),
    }));
  }, [nodes, columnLayout]);

  const moveColumnToRow = (columnKey, targetRow) => {
    onChangeColumnLayout((prev) => ({
      ...prev,
      [columnKey]: { row: targetRow },
    }));

    // força recálculo das posições
    requestAnimationFrame(() => {
      if (boardRef.current) {
        setBoardRect(boardRef.current.getBoundingClientRect());
      }
    });
  };

  /* =============================
     INTERAÇÃO
     ============================= */

  const handleNodeClick = (node) => {
    if (!selectedNodeId) {
      setSelectedNodeId(node.id);
      return;
    }

    if (selectedNodeId === node.id) {
      setSelectedNodeId(null);
      return;
    }

    onChangeConnections((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        from: selectedNodeId,
        to: node.id,
        type: "maintains",
        createdAt: new Date(),
      },
    ]);

    setSelectedNodeId(null);
  };

  const clearBoard = () => {
    onChangeConnections([]);
    setSelectedNodeId(null);
  };

  const handleRemoveConnection = (connectionId) => {
    onChangeConnections((prev) => prev.filter((c) => c.id !== connectionId));
  };

  /* =============================
     ALTURA SVG (2 ROWS)
     ============================= */

  const maxNodesPerColumn = Math.max(
    ...nodesByColumn.map((c) => c.nodes.length),
    1
  );

  /* =============================
     RENDER
     ============================= */

  return (
    <Modal onClose={onClose} title="Campo Funcional de Processos">
      <div className={styles.wrapper}>
        <div className={styles.board} ref={boardRef}>
          <header className={styles.header}>
            <Button variant="outline" onClick={clearBoard}>
              Limpar quadro
            </Button>
            <Button
              variant="outline"
              onClick={exportAsImage}
              title="Exportar mapa como imagem"
            >
              Exportar imagem
            </Button>
            <Button
              variant="outline"
              onClick={toggleFullscreen}
              title={
                isFullscreen ? "Sair do fullscreen" : "Abrir em tela cheia"
              }
            >
              {isFullscreen ? "Sair do fullscreen" : "Fullscreen"}
            </Button>
          </header>
          {/* ===== SVG ===== */}
          <svg
            className={styles.overlay}
            width="100%"
            height={boardRect?.height || "100%"}
          >
            {connections.map((c) => {
              const fromEl = nodeRefs.current[c.from];
              const toEl = nodeRefs.current[c.to];
              if (!fromEl || !toEl || !boardRect) return null;

              const fromRect = fromEl.getBoundingClientRect();
              const toRect = toEl.getBoundingClientRect();

              const x1 = fromRect.left + fromRect.width / 2 - boardRect.left;
              const y1 = fromRect.top + fromRect.height / 2 - boardRect.top;

              const x2 = toRect.left + toRect.width / 2 - boardRect.left;
              const y2 = toRect.top + toRect.height / 2 - boardRect.top;

              return (
                <line
                  key={c.id}
                  x1={x1}
                  y1={y1}
                  x2={x2}
                  y2={y2}
                  stroke="#6b7280"
                  strokeWidth="2"
                  strokeDasharray="5"
                  strokeLinecap="round"
                  opacity="0.6"
                  style={{ cursor: "pointer" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveConnection(c.id);
                  }}
                  onMouseEnter={(e) =>
                    e.target.setAttribute("stroke", "#ef4444")
                  }
                  onMouseLeave={(e) =>
                    e.target.setAttribute("stroke", "#6b7280")
                  }
                />
              );
            })}
          </svg>

          {/* ===== ROWS ===== */}
          <div className={styles.rows}>
            {[0, 1].map((rowIndex) => (
              <div key={rowIndex} className={styles.row}>
                <div
                  className={styles.columns}
                  style={{
                    gridTemplateColumns: `repeat(${COLUMNS.length}, 1fr)`,
                    gap: COLUMN_GAP,
                  }}
                >
                  {nodesByColumn.map((col) =>
                    col.row !== rowIndex ? (
                      <div key={col.key} />
                    ) : (
                      <div key={col.key} className={styles.column}>
                        <div className={styles.columnHeader}>
                          <h4>{col.title}</h4>

                          <div className={styles.columnActions}>
                            {col.row === 0 && (
                              <button
                                className={styles.moveBtn}
                                title="Mover para linha inferior"
                                onClick={() => moveColumnToRow(col.key, 1)}
                              >
                                ↓
                              </button>
                            )}

                            {col.row === 1 && (
                              <button
                                className={styles.moveBtn}
                                title="Mover para linha superior"
                                onClick={() => moveColumnToRow(col.key, 0)}
                              >
                                ↑
                              </button>
                            )}
                          </div>
                        </div>

                        {col.nodes.length === 0 && (
                          <p className={styles.empty}>Nenhum elemento</p>
                        )}

                        {col.nodes.map((node) => {
                          const selected = selectedNodeId === node.id;

                          return (
                            <div
                              key={node.id}
                              ref={(el) => {
                                if (el) nodeRefs.current[node.id] = el;
                              }}
                              className={`${styles.node} ${
                                selected ? styles.selected : ""
                              }`}
                              style={{
                                borderLeft: node.processTag
                                  ? `4px solid ${
                                      PROCESS_COLORS[node.processTag]
                                    }`
                                  : "4px solid",
                              }}
                              onClick={() => handleNodeClick(node)}
                            >
                              {node.text}
                            </div>
                          );
                        })}
                      </div>
                    )
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
}
